import { api, Recipe, RecipeFull, Category, AmountUnit, ApiError, RecipeUsageEntry, escapeHtml, renderError } from "../api.js";
import { scanAndLookup, scanBarcode, searchOpenFoodFactsByName, OffProduct } from "../scanner.js";

type SortMode = "name" | "kcal" | "recent";

type FormMode =
    | { kind: "create"; prefill?: Partial<Recipe> }
    | { kind: "edit"; recipe: RecipeFull };

// Liest ein File-Image, rendert auf Canvas mit max-Seite 1024, gibt JPEG-Blob (qual 0.85) zurueck.
async function resizeToJpeg (file: File, maxDim = 1024, quality = 0.85): Promise<Blob> {
    const dataUrl = await new Promise<string> ((resolve, reject) => {
        const r = new FileReader ();
        r.onload = () => resolve (r.result as string);
        r.onerror = () => reject (r.error);
        r.readAsDataURL (file);
    });

    const img = await new Promise<HTMLImageElement> ((resolve, reject) => {
        const i = new Image ();
        i.onload = () => resolve (i);
        i.onerror = () => reject (new Error ("Bild konnte nicht geladen werden"));
        i.src = dataUrl;
    });

    let { width, height } = img;
    if (width > maxDim || height > maxDim) {
        const ratio = Math.min (maxDim / width, maxDim / height);
        width = Math.round (width * ratio);
        height = Math.round (height * ratio);
    }

    const canvas = document.createElement ("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext ("2d");
    if (ctx === null) throw new Error ("Canvas 2D-Kontext nicht verfuegbar");
    ctx.drawImage (img, 0, 0, width, height);

    return new Promise<Blob> ((resolve, reject) => {
        canvas.toBlob ((blob) => {
            if (blob === null) reject (new Error ("Bild-Encoding fehlgeschlagen"));
            else resolve (blob);
        }, "image/jpeg", quality);
    });
}

export async function renderRecipes (root: HTMLElement): Promise<void> {
    const header = document.createElement ("div");
    header.className = "row";

    const title = document.createElement ("h2");
    title.textContent = "Rezepte";
    title.style.margin = "0";
    title.style.flex = "1 1 auto";

    const categorySelect = document.createElement ("sl-select");
    categorySelect.setAttribute ("size", "small");
    categorySelect.setAttribute ("value", "food");
    categorySelect.style.flex = "0 0 130px";
    categorySelect.innerHTML = `
        <sl-option value="food">Essen</sl-option>
        <sl-option value="drink">Getraenk</sl-option>
    `;

    const sortSelect = document.createElement ("sl-select");
    sortSelect.setAttribute ("size", "small");
    sortSelect.setAttribute ("value", "name");
    sortSelect.style.flex = "0 0 150px";
    sortSelect.innerHTML = `
        <sl-option value="name">Sort: Name</sl-option>
        <sl-option value="kcal">Sort: kcal</sl-option>
        <sl-option value="recent">Sort: Zuletzt</sl-option>
    `;

    const scanBtn = document.createElement ("sl-button");
    scanBtn.setAttribute ("size", "small");
    scanBtn.textContent = "Barcode";
    scanBtn.style.flex = "0 0 auto";

    const addBtn = document.createElement ("sl-button");
    addBtn.setAttribute ("variant", "primary");
    addBtn.setAttribute ("size", "small");
    addBtn.textContent = "+ Neu";
    addBtn.style.flex = "0 0 auto";

    header.appendChild (title);
    header.appendChild (categorySelect);
    header.appendChild (sortSelect);
    header.appendChild (scanBtn);
    header.appendChild (addBtn);

    const searchInput = document.createElement ("sl-input");
    searchInput.setAttribute ("placeholder", "Suche in Name + Beschreibung ...");
    searchInput.setAttribute ("type", "search");
    searchInput.setAttribute ("clearable", "");
    searchInput.style.marginTop = "0.75rem";

    const list = document.createElement ("div");
    list.className = "stack";
    list.style.marginTop = "1rem";

    const dialog = document.createElement ("sl-dialog");

    root.appendChild (header);
    root.appendChild (searchInput);
    root.appendChild (list);
    root.appendChild (dialog);

    async function reload (): Promise<void> {
        list.innerHTML = "";
        const spinner = document.createElement ("sl-spinner");
        list.appendChild (spinner);

        try {
            const q = (searchInput as unknown as { value: string }).value.trim ();
            const cat = (categorySelect as unknown as { value: Category }).value;
            const sortMode = (sortSelect as unknown as { value: SortMode }).value;

            let recipes: Recipe[];
            if (q.length >= 2) {
                recipes = await api.searchRecipes (q);
            }
            else {
                recipes = await api.listRecipes (cat);
                if (sortMode === "kcal") {
                    recipes.sort ((a, b) => b.kcal - a.kcal);
                }
                else if (sortMode === "recent") {
                    const recent = await api.recentRecipes ();
                    const recentIds = new Map<number, number> ();
                    recent.forEach ((r, i) => recentIds.set (r.recipe_id, i));
                    recipes.sort ((a, b) => {
                        const ai = recentIds.get (a.recipe_id);
                        const bi = recentIds.get (b.recipe_id);
                        if (ai !== undefined && bi !== undefined) return ai - bi;
                        if (ai !== undefined) return -1;
                        if (bi !== undefined) return 1;
                        return a.name.localeCompare (b.name);
                    });
                }
                // 'name' ist der Default vom Backend-Listing
            }

            list.innerHTML = "";

            categorySelect.toggleAttribute ("disabled", q.length >= 2);
            sortSelect.toggleAttribute ("disabled", q.length >= 2);

            if (recipes.length === 0) {
                const empty = document.createElement ("div");
                empty.className = "muted";
                empty.textContent = q.length >= 2 ? "Keine Treffer." : "Keine Eintraege.";
                list.appendChild (empty);
                return;
            }

            for (const r of recipes) {
                list.appendChild (recipeCard (r, openEditDialog, confirmAndDelete));
            }
        }
        catch (err) {
            list.innerHTML = "";
            const e = document.createElement ("div");
            e.className = "error";
            e.textContent = (err instanceof Error) ? err.message : "Fehler beim Laden";
            list.appendChild (e);
        }
    }

    function openCreateDialog (prefill?: Partial<Recipe>): void {
        dialog.setAttribute ("label", prefill !== undefined ? "Neues Rezept (vom Barcode)" : "Neues Rezept");
        dialog.innerHTML = "";
        dialog.appendChild (recipeForm (
            (categorySelect as unknown as { value: Category }).value,
            { kind: "create", prefill },
            async () => {
                (dialog as unknown as { hide: () => void }).hide ();
                await reload ();
            },
            openCloneDialog,
        ));
        (dialog as unknown as { show: () => void }).show ();
    }

    async function openEditDialog (r: Recipe): Promise<void> {
        dialog.setAttribute ("label", `Rezept bearbeiten — ${r.name}`);
        dialog.innerHTML = "<sl-spinner></sl-spinner>";
        (dialog as unknown as { show: () => void }).show ();

        try {
            const full = await api.getRecipe (r.recipe_id);
            dialog.innerHTML = "";
            dialog.appendChild (recipeForm (
                full.category,
                { kind: "edit", recipe: full },
                async () => {
                    (dialog as unknown as { hide: () => void }).hide ();
                    await reload ();
                },
                openCloneDialog,
            ));
        }
        catch (err) {
            renderError (dialog, err);
        }
    }

    async function openScanDialog (): Promise<void> {
        const result = await scanAndLookup ();

        if (result === null) {
            return;
        }

        if (result.kind === "existing") {
            await openEditDialog (result.recipe);
        }
        else {
            openCreateDialog (result.prefill);
        }
    }

    function openCloneDialog (prefill: Partial<Recipe>): void {
        // close any current dialog first
        (dialog as unknown as { hide: () => void }).hide ();
        window.setTimeout (() => openCreateDialog (prefill), 250);
    }

    async function confirmAndDelete (r: Recipe): Promise<void> {
        if (!window.confirm (`Rezept "${r.name}" wirklich loeschen?`)) {
            return;
        }

        try {
            await api.deleteRecipe (r.recipe_id);
            await reload ();
        }
        catch (err) {
            const msg = (err instanceof ApiError) ? err.message : "Fehler beim Loeschen";
            window.alert (msg);
        }
    }

    let searchTimer: number | undefined;
    searchInput.addEventListener ("sl-input", () => {
        if (searchTimer !== undefined) {
            window.clearTimeout (searchTimer);
        }
        searchTimer = window.setTimeout (() => { void reload (); }, 250);
    });

    categorySelect.addEventListener ("sl-change", () => { void reload (); });
    sortSelect.addEventListener ("sl-change", () => { void reload (); });
    addBtn.addEventListener ("click", () => openCreateDialog ());
    scanBtn.addEventListener ("click", () => { void openScanDialog (); });

    await reload ();
}

// Pro <img> eine Ladegeneration: wird loadPhotoInto auf demselben Element
// ueberlappend aufgerufen (z.B. Edit-Preview oeffnen und sofort hochladen),
// verwirft eine zu spaet ankommende, veraltete Antwort ihre object-URL, statt
// das aktuelle Bild zu ueberschreiben.
const g_photoGen = new WeakMap<HTMLImageElement, number> ();

// Laedt das Rezept-Foto mit Auth-Header (nicht Token-in-URL) und haengt es als
// kurzlebige object-URL ins <img>. Die URL wird nach dem Laden wieder
// freigegeben (kein Memory-Leak). Bei fehlendem Foto / Fehler bleibt der
// Platzhalter stehen.
function loadPhotoInto (img: HTMLImageElement, recipeId: number): void {
    const gen = (g_photoGen.get (img) ?? 0) + 1;
    g_photoGen.set (img, gen);
    api.recipePhotoObjectUrl (recipeId)
        .then ((url) => {
            if (url === null) return;
            // Veraltete Antwort (fuer dieses <img> wurde inzwischen erneut
            // geladen)? object-URL freigeben und nichts setzen.
            if (g_photoGen.get (img) !== gen) { URL.revokeObjectURL (url); return; }
            // Eine evtl. vorher gesetzte blob:-URL desselben <img> freigeben,
            // bevor sie ueberschrieben wird (z.B. Edit-Preview nach Upload).
            if (img.src.startsWith ("blob:")) URL.revokeObjectURL (img.src);
            // Nach Anzeige ODER Fehler freigeben — sonst leakt die object-URL,
            // wenn das <img> vor 'load' aus dem DOM faellt (schnelles Re-Render
            // der Liste beim Tippen in der Suche) oder das Dekodieren scheitert.
            const revoke = () => URL.revokeObjectURL (url);
            img.addEventListener ("load", revoke, { once: true });
            img.addEventListener ("error", revoke, { once: true });
            img.src = url;
        })
        .catch (() => undefined);
}

function recipeCard (
    r: Recipe,
    onEdit: (r: Recipe) => Promise<void>,
    onDelete: (r: Recipe) => Promise<void>,
): HTMLElement {
    const card = document.createElement ("sl-card");

    const outer = document.createElement ("div");
    outer.style.display = "flex";
    outer.style.gap = "0.75rem";
    outer.style.alignItems = "flex-start";

    if (r.has_photo === true) {
        const img = document.createElement ("img");
        loadPhotoInto (img, r.recipe_id);
        img.alt = "";
        img.loading = "lazy";
        img.style.width = "60px";
        img.style.height = "60px";
        img.style.objectFit = "cover";
        img.style.borderRadius = "4px";
        img.style.flex = "0 0 auto";
        outer.appendChild (img);
    }

    const wrap = document.createElement ("div");
    wrap.style.flex = "1 1 auto";
    wrap.style.minWidth = "0";

    const head = document.createElement ("div");
    head.className = "row";

    const name = document.createElement ("strong");
    name.textContent = r.name;
    name.style.flex = "1 1 auto";

    const kcal = document.createElement ("span");
    kcal.className = "muted";
    kcal.textContent = `${r.kcal} kcal / ${r.reference_amount} ${unitLabel (r.reference_unit)}`;
    kcal.style.flex = "0 0 auto";

    const editBtn = document.createElement ("sl-icon-button");
    editBtn.setAttribute ("name", "pencil");
    editBtn.setAttribute ("label", "Bearbeiten");
    editBtn.style.flex = "0 0 auto";
    editBtn.addEventListener ("click", () => { void onEdit (r); });

    const delBtn = document.createElement ("sl-icon-button");
    delBtn.setAttribute ("name", "trash");
    delBtn.setAttribute ("label", "Loeschen");
    delBtn.style.flex = "0 0 auto";
    delBtn.addEventListener ("click", () => { void onDelete (r); });

    head.appendChild (name);
    head.appendChild (kcal);
    head.appendChild (editBtn);
    head.appendChild (delBtn);

    const macros = document.createElement ("div");
    macros.className = "muted";
    macros.textContent = `Eiweiss ${fmt (r.protein)} g — Fett ${fmt (r.fat)} g — Zucker ${fmt (r.sugar)} g`;
    macros.style.marginTop = "0.25rem";

    wrap.appendChild (head);
    wrap.appendChild (macros);
    outer.appendChild (wrap);
    card.appendChild (outer);

    return card;
}

async function showRecipeUsage (recipeId: number, recipeName: string): Promise<void> {
    const dialog = document.createElement ("sl-dialog");
    dialog.setAttribute ("label", `Verlauf — ${recipeName}`);

    const wrap = document.createElement ("div");
    wrap.innerHTML = "<sl-spinner></sl-spinner>";
    dialog.appendChild (wrap);

    document.body.appendChild (dialog);
    (dialog as unknown as { show: () => void }).show ();

    dialog.addEventListener ("sl-after-hide", () => {
        window.setTimeout (() => dialog.remove (), 400);
    });

    let usage: RecipeUsageEntry[] = [];
    try {
        usage = await api.getRecipeUsage (recipeId);
    }
    catch (err) {
        renderError (wrap, err);
        return;
    }

    wrap.innerHTML = "";

    if (usage.length === 0) {
        const empty = document.createElement ("div");
        empty.className = "muted";
        empty.textContent = "Noch nicht protokolliert.";
        wrap.appendChild (empty);
        return;
    }

    const table = document.createElement ("table");
    table.className = "totals";
    table.style.width = "100%";
    let html = `<thead><tr>
        <th style="text-align:left">Datum</th>
        <th style="text-align:left">Mahlzeit</th>
        <th style="text-align:right">Menge</th>
    </tr></thead><tbody>`;
    for (const u of usage) {
        const d = new Date (u.consumed_at * 1000);
        const date = d.toLocaleDateString ("de-DE", { day: "2-digit", month: "2-digit", year: "2-digit" });
        const time = d.toLocaleTimeString ("de-DE", { hour: "2-digit", minute: "2-digit" });
        const meal = u.meal_type;
        html += `<tr>
            <td>${date} ${time}</td>
            <td>${escapeHtml (mealLabel (meal))}</td>
            <td style="text-align:right">${u.amount} ${unitLabel (u.amount_unit)}</td>
        </tr>`;
    }
    html += "</tbody>";
    table.innerHTML = html;
    wrap.appendChild (table);
}

function mealLabel (m: string): string {
    switch (m) {
        case "breakfast": return "Fruehstueck";
        case "lunch":     return "Mittag";
        case "dinner":    return "Abendessen";
        case "snack":     return "Snack";
        case "drink":     return "Getraenk";
        default:          return m;
    }
}

function unitLabel (u: AmountUnit): string {
    switch (u) {
        case "gram":       return "g";
        case "milliliter": return "ml";
        case "piece":      return "Stueck";
        case "portion":    return "Portion";
    }
}

function fmt (n: number): string {
    return Number.isInteger (n) ? String (n) : n.toFixed (1);
}

function recipeForm (
    defaultCategory: Category,
    mode: FormMode,
    onSaved: () => Promise<void>,
    onClone: (prefill: Partial<Recipe>) => void,
): HTMLElement {
    const isEdit = mode.kind === "edit";
    const source: Partial<Recipe> & { description?: string | null } =
        mode.kind === "edit" ? mode.recipe : (mode.prefill ?? {});

    const form = document.createElement ("div");
    form.className = "stack";

    const nameRow = document.createElement ("div");
    nameRow.style.display = "flex";
    nameRow.style.gap = "0.5rem";
    nameRow.style.alignItems = "end";

    const nameIn = mkInput ("Name", "text", source.name);
    nameIn.style.flex = "1 1 auto";

    const offSearchBtn = document.createElement ("sl-button");
    offSearchBtn.textContent = "OFF-Suche";
    offSearchBtn.style.flex = "0 0 auto";
    offSearchBtn.addEventListener ("click", async () => {
        const val = (nameIn as unknown as { value: string }).value.trim ();
        if (val.length < 2) {
            window.alert ("Bitte zuerst einen Namen (>=2 Zeichen) eintippen.");
            return;
        }
        offSearchBtn.setAttribute ("loading", "");
        try {
            const hits = await searchOpenFoodFactsByName (val);
            if (hits.length === 0) {
                window.alert ("Keine Treffer bei Open Food Facts.");
                return;
            }
            const picked = await pickOffResult (hits);
            if (picked !== null) {
                const set = (el: HTMLElement, v: string) => { (el as unknown as { value: string }).value = v; };
                set (nameIn, picked.name);
                set (kcal, String (picked.kcal));
                set (protein, String (picked.protein));
                set (fat, String (picked.fat));
                set (sugar, String (picked.sugar));
            }
        }
        finally {
            offSearchBtn.removeAttribute ("loading");
        }
    });

    nameRow.appendChild (nameIn);
    nameRow.appendChild (offSearchBtn);

    const descIn = document.createElement ("sl-textarea");
    descIn.setAttribute ("label", "Beschreibung");
    descIn.setAttribute ("rows", "2");

    if (source.description !== undefined && source.description !== null) {
        descIn.setAttribute ("value", source.description);
    }

    const catIn = document.createElement ("sl-select");
    catIn.setAttribute ("label", "Kategorie");
    catIn.setAttribute ("value", source.category ?? defaultCategory);
    catIn.innerHTML = `
        <sl-option value="food">Essen</sl-option>
        <sl-option value="drink">Getraenk</sl-option>
    `;

    const refAmount = mkInput ("Referenz-Menge", "number", String (source.reference_amount ?? 100));

    const refUnit = document.createElement ("sl-select");
    refUnit.setAttribute ("label", "Einheit");
    refUnit.setAttribute ("value", source.reference_unit ?? "gram");
    refUnit.innerHTML = `
        <sl-option value="gram">g</sl-option>
        <sl-option value="milliliter">ml</sl-option>
        <sl-option value="piece">Stueck</sl-option>
        <sl-option value="portion">Portion</sl-option>
    `;

    const protein = mkInput ("Eiweiss (g)", "number", String (source.protein ?? 0));
    const fat = mkInput ("Fett (g)", "number", String (source.fat ?? 0));
    const sugar = mkInput ("Zucker (g)", "number", String (source.sugar ?? 0));
    const kcal = mkInput ("kcal", "number", String (source.kcal ?? 0));

    // Barcode-Feld mit inline Scan-Button.
    const barcodeRow = document.createElement ("div");
    barcodeRow.style.display = "flex";
    barcodeRow.style.alignItems = "end";
    barcodeRow.style.gap = "0.5rem";

    const barcodeIn = mkInput ("Barcode / QR-Code (optional)", "text", source.barcode ?? "");
    barcodeIn.style.flex = "1 1 auto";

    const barcodeScan = document.createElement ("sl-button");
    barcodeScan.textContent = "Scannen";
    barcodeScan.style.flex = "0 0 auto";
    barcodeScan.addEventListener ("click", async () => {
        const code = await scanBarcode ();
        if (code !== null && code !== "") {
            (barcodeIn as unknown as { value: string }).value = code;
        }
    });

    barcodeRow.appendChild (barcodeIn);
    barcodeRow.appendChild (barcodeScan);

    // Photo-Sektion: nur im Edit-Modus aktiv (vorher kein recipe_id).
    const photoSection = document.createElement ("div");
    photoSection.className = "stack";
    photoSection.style.padding = "0.5rem";
    photoSection.style.border = "1px solid var(--sl-color-neutral-200, #e5e7eb)";
    photoSection.style.borderRadius = "4px";

    const photoTitle = document.createElement ("div");
    photoTitle.style.fontWeight = "600";
    photoTitle.textContent = "Foto";
    photoSection.appendChild (photoTitle);

    if (mode.kind === "edit") {
        const recipeId = mode.recipe.recipe_id;
        const hasPhoto = mode.recipe.has_photo === true;

        const preview = document.createElement ("img");
        preview.alt = "";
        preview.style.maxWidth = "200px";
        preview.style.maxHeight = "200px";
        preview.style.objectFit = "cover";
        preview.style.borderRadius = "4px";
        preview.style.display = hasPhoto ? "block" : "none";
        if (hasPhoto) {
            loadPhotoInto (preview, recipeId);
        }
        photoSection.appendChild (preview);

        const file = document.createElement ("input");
        file.type = "file";
        file.accept = "image/*";

        const uploadStatus = document.createElement ("div");
        uploadStatus.className = "muted";
        uploadStatus.style.fontSize = "0.875rem";

        file.addEventListener ("change", async () => {
            const f = file.files?.[0];
            if (f === undefined) return;
            uploadStatus.className = "muted";
            uploadStatus.textContent = "Resize + Upload ...";
            try {
                const blob = await resizeToJpeg (f);
                await api.uploadRecipePhoto (recipeId, blob);
                loadPhotoInto (preview, recipeId);
                preview.style.display = "block";
                uploadStatus.textContent = "Foto hochgeladen.";
                file.value = "";
            }
            catch (err) {
                uploadStatus.className = "error";
                uploadStatus.textContent = (err instanceof Error) ? err.message : "Upload-Fehler";
            }
        });

        const delPhoto = document.createElement ("sl-button");
        delPhoto.setAttribute ("size", "small");
        delPhoto.textContent = "Foto entfernen";
        delPhoto.style.display = hasPhoto ? "inline-block" : "none";
        delPhoto.addEventListener ("click", async () => {
            try {
                await api.deleteRecipePhoto (recipeId);
                preview.style.display = "none";
                delPhoto.style.display = "none";
                uploadStatus.className = "muted";
                uploadStatus.textContent = "Foto entfernt.";
            }
            catch (err) {
                uploadStatus.className = "error";
                uploadStatus.textContent = (err instanceof Error) ? err.message : "Fehler";
            }
        });

        photoSection.appendChild (file);
        photoSection.appendChild (delPhoto);
        photoSection.appendChild (uploadStatus);
    }
    else {
        const hint = document.createElement ("div");
        hint.className = "muted";
        hint.style.fontSize = "0.875rem";
        hint.textContent = "Foto kann nach dem ersten Speichern hochgeladen werden.";
        photoSection.appendChild (hint);
    }

    const error = document.createElement ("div");
    error.className = "error";

    const actions = document.createElement ("div");
    actions.className = "row";
    actions.style.justifyContent = "flex-end";

    const collectPayload = () => {
        const val = (el: HTMLElement): string => (el as unknown as { value: string }).value;
        const num = (el: HTMLElement): number => parseFloat (val (el)) || 0;
        return {
            name: val (nameIn).trim (),
            description: val (descIn).trim () || null,
            category: val (catIn) as Category,
            reference_amount: num (refAmount),
            reference_unit: val (refUnit) as AmountUnit,
            protein: num (protein),
            fat: num (fat),
            sugar: num (sugar),
            kcal: Math.round (num (kcal)),
            barcode: val (barcodeIn).trim () || null,
        };
    };

    // Klonen + Verlauf nur im Edit-Modus.
    if (isEdit && mode.kind === "edit") {
        const recipeIdEdit = mode.recipe.recipe_id;
        const recipeNameEdit = mode.recipe.name;

        const history = document.createElement ("sl-button");
        history.textContent = "Verlauf";
        history.style.flex = "0 0 auto";
        history.addEventListener ("click", () => { void showRecipeUsage (recipeIdEdit, recipeNameEdit); });
        actions.appendChild (history);

        const clone = document.createElement ("sl-button");
        clone.textContent = "Klonen";
        clone.style.flex = "0 0 auto";
        clone.addEventListener ("click", () => {
            const p = collectPayload ();
            // Beim Klonen: name " (Kopie)", barcode entfernen (unique constraint).
            onClone ({
                name: `${p.name} (Kopie)`,
                description: p.description,
                category: p.category,
                reference_amount: p.reference_amount,
                reference_unit: p.reference_unit,
                protein: p.protein,
                fat: p.fat,
                sugar: p.sugar,
                kcal: p.kcal,
                // barcode bewusst weggelassen
            });
        });
        actions.appendChild (clone);
    }

    const submit = document.createElement ("sl-button");
    submit.setAttribute ("variant", "primary");
    submit.textContent = isEdit ? "Speichern" : "Anlegen";
    submit.style.flex = "0 0 auto";
    submit.addEventListener ("click", async () => {
        const payload = collectPayload ();

        if (payload.name === "") {
            error.textContent = "Name ist erforderlich";
            return;
        }

        submit.setAttribute ("loading", "");
        error.textContent = "";

        try {
            if (mode.kind === "edit") {
                await api.updateRecipe (mode.recipe.recipe_id, payload);
            }
            else {
                await api.createRecipe (payload);
            }
            await onSaved ();
        }
        catch (err) {
            error.textContent = (err instanceof Error) ? err.message : "Fehler beim Speichern";
        }
        finally {
            submit.removeAttribute ("loading");
        }
    });

    actions.appendChild (submit);

    [nameRow, descIn, catIn, refAmount, refUnit, protein, fat, sugar, kcal, barcodeRow, photoSection, error, actions]
        .forEach (el => form.appendChild (el));

    return form;
}

function pickOffResult (hits: OffProduct[]): Promise<OffProduct | null> {
    return new Promise<OffProduct | null> ((resolve) => {
        const dialog = document.createElement ("sl-dialog");
        dialog.setAttribute ("label", `Open Food Facts — ${hits.length} Treffer`);

        const wrap = document.createElement ("div");
        wrap.className = "stack";

        let chosen: OffProduct | null = null;

        for (const h of hits) {
            const btn = document.createElement ("sl-button");
            btn.style.justifyContent = "flex-start";
            btn.style.textAlign = "left";
            btn.style.width = "100%";
            btn.innerHTML = `<div style="display:flex;flex-direction:column;align-items:flex-start;width:100%;">
                <strong>${escapeHtml (h.name)}</strong>
                <span style="font-size:0.75rem;opacity:.7;">${h.kcal} kcal / Eiweiss ${h.protein.toFixed (1)} g / Fett ${h.fat.toFixed (1)} g / Zucker ${h.sugar.toFixed (1)} g</span>
            </div>`;
            btn.addEventListener ("click", () => {
                chosen = h;
                (dialog as unknown as { hide: () => void }).hide ();
            });
            wrap.appendChild (btn);
        }

        const cancel = document.createElement ("sl-button");
        cancel.setAttribute ("variant", "text");
        cancel.textContent = "Abbrechen";
        cancel.addEventListener ("click", () => {
            (dialog as unknown as { hide: () => void }).hide ();
        });
        wrap.appendChild (cancel);

        dialog.appendChild (wrap);
        document.body.appendChild (dialog);
        dialog.addEventListener ("sl-after-hide", () => {
            window.setTimeout (() => dialog.remove (), 400);
            resolve (chosen);
        });
        (dialog as unknown as { show: () => void }).show ();
    });
}

function mkInput (label: string, type: string, value?: string): HTMLElement {
    const i = document.createElement ("sl-input");
    i.setAttribute ("label", label);
    i.setAttribute ("type", type);
    if (value !== undefined) i.setAttribute ("value", value);
    return i;
}
